import {
  CloseIcon,
  CurrencyEnum,
  FormInput,
  Hint,
  PrimaryButton,
  SaveIcon,
  toast,
} from '@systemeio/ui'
import { BadRequest, InternalError } from '@systemeio/util'
import { useUserPricePlans } from 'modules/digital-product/price-plans/hooks/use-user-price-plans'
import { PricePlanEditInterface } from 'modules/digital-product/price-plans/types/price-plan-interface'
import React, { useEffect, useState } from 'react'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { PricePlanType } from '@systemeio/types'

interface EditPricePlanFormProps {
  onEditPricePlan: (value: PricePlanEditInterface) => Promise<void>
  closeForm: () => void
  editPricePlanErrors: PricePlanEditInterface
  pricePlanId?: PricePlanType['id']
  currency: CurrencyEnum
}

const defaultValues: PricePlanEditInterface = {
  name: '',
  innerName: '',
  statementDescriptor: '',
}

function EditPricePlanForm({
  onEditPricePlan,
  closeForm,
  editPricePlanErrors,
  pricePlanId,
  currency,
}: EditPricePlanFormProps) {
  const { t } = useLocoTranslation()
  const [tempState, setTempState] = useState(defaultValues)
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState('')

  const { userPricePlans } = useUserPricePlans(currency)

  const handleConfirm = async () => {
    try {
      setError('')
      setIsFetching(true)
      await onEditPricePlan(tempState)
      setIsFetching(false)
      closeForm()
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        if (e.errors.common) {
          setError(e.errors.common.join(''))
        }
      } else if (e instanceof InternalError) {
        setError(t('core.error.internal_error_message'))
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  useEffect(() => {
    const pricePlan = userPricePlans?.find(pricePlan => pricePlan.id === pricePlanId)
    if (pricePlan) {
      setTempState(prev => {
        return {
          ...prev,
          name: pricePlan.name,
          innerName: pricePlan.innerName,
          statementDescriptor: pricePlan.statementDescriptor,
        }
      })
    }
  }, [pricePlanId, userPricePlans])

  return (
    <div className="border-text-secondary/30 relative flex flex-col gap-5 rounded-lg border px-5 py-3">
      <CloseIcon
        className={`cursor-pointer ${
          isFetching && 'fill-text-secondary pointer-events-none'
        } absolute right-1 top-1 lg:right-2 lg:top-2`}
        onClick={() => closeForm()}
      />
      <FormInput
        required
        isPreFetching={!pricePlanId}
        label={t('dashboard.funnel.configuration.common.price_plan.name_label')}
        labelClassName={'lg:text-[15px] text-sm text-text-primary font-medium'}
        value={tempState.name}
        onChange={e =>
          setTempState(prev => {
            return { ...prev, name: e.target.value }
          })
        }
        maxLength={256}
        error={editPricePlanErrors.name}
      />
      <FormInput
        required
        isPreFetching={!pricePlanId}
        label={t('dashboard.funnel.configuration.common.price_plan.inner_name_label')}
        labelClassName={'lg:text-[15px] text-sm text-text-primary font-medium'}
        value={tempState.innerName}
        onChange={e =>
          setTempState(prev => {
            return { ...prev, innerName: e.target.value }
          })
        }
        maxLength={256}
        error={editPricePlanErrors.innerName}
      />
      <FormInput
        required
        isPreFetching={!pricePlanId}
        label={
          <Hint
            label={t(
              'dashboard.funnel.configuration.common.price_plan.statement_descriptor_popover',
            )}
          >
            {t('dashboard.funnel.configuration.common.price_plan.statement_descriptor')}
          </Hint>
        }
        labelClassName={'lg:text-[15px] text-sm text-text-primary font-medium'}
        value={tempState.statementDescriptor}
        onChange={e =>
          setTempState(prev => {
            return { ...prev, statementDescriptor: e.target.value }
          })
        }
        maxLength={256}
        error={editPricePlanErrors.statementDescriptor}
      />
      <div className="flex ">
        <PrimaryButton
          disabled={!tempState.name || !tempState.innerName || !tempState.statementDescriptor}
          width="large"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault()
            handleConfirm()
          }}
          isFetching={isFetching}
          type="submit"
        >
          <SaveIcon />
          {t('global.save')}
        </PrimaryButton>
      </div>
      <FieldErrorAndDescription error={error} errorClassName={'text-center mt-2'} />
    </div>
  )
}

export default EditPricePlanForm
